import { getCycle, getOperatingZone, getRuleSetById } from "hos-tools";
import AppView from "../../../app.view";
import DutyStatusLog from "../../../model/DutyStatusLog";
import { dstCalculator } from "../../../utils/date";
import dayjs from "../../../utils/dayjs";
import { hashToArray } from "../../../utils/vanilla";
import AvailabilityDisplayHelper, { type IAvailabilityDisplayItem } from "../../../modules/hos/hosMain/helpers/availabilityDisplayHelper";
import { SyncType } from "../../../sync/sync.types";
import { ZoneCycleChangeDialogTranslationsDictionary } from "../modals/zoneCycleChange/translations";
import { isDriverViewingInspectionMode } from "../../../utils/uri";
import type { ViolationValidationResult } from "../../../utils/violations";

export type { IAvailabilityDisplayItem };

interface IChangeLogDictionary {
    [dateString: string]: {
        formattedDate: string;
        entries: IChangeLogEntry[];
    };
}

export interface IChangeLogEntry {
    type: "zone" | "cycle";
    label: string;
    value: string;
}

export interface IChangeLogDisplay {
    formattedDate: string;
    entries: IChangeLogEntry[];
}

export interface IZoneCycleDialogData {
    changeLogList: IChangeLogDisplay[];
    violationWarnings: string;
    availabilityItems: IAvailabilityDisplayItem[];
    logsToVerify: DutyStatusLog[];
}

/**
 * Determines if the zone cycle change dialog should be shown based on current state.
 * Returns false if:
 * - No content view
 * - Driver is viewing inspection mode
 * - Dialog already exists
 * - No logs to verify
 * - Rulesets not yet downloaded
 *
 * @param app - The AppView instance
 * @param logsToVerify - The logs that need verification
 * @returns Whether the dialog should be shown
 */
export function shouldShowZoneCycleDialog(
    app: AppView,
    logsToVerify: DutyStatusLog[],
    isZenithContext = false
): boolean {
    // For Backbone context, require contentView to be set
    // For Zenith/React context, contentView may be undefined (React pages don't set it)
    if (!isZenithContext && !app.contentView) {
        return false;
    }

    if (isDriverViewingInspectionMode() || document.getElementById("new-zoneCycle-changes-dialog")) {
        return false;
    }

    if (!logsToVerify.length) {
        return false;
    }

    const rulesets = app.hosUtils.getUserHosRuleSets();
    for (let i = 0; i < logsToVerify.length; i++) {
        const cycleOperatingZoneLog = logsToVerify[i];
        if (!cycleOperatingZoneLog.userHosRuleSet?.id) {
            continue;
        }

        const ruleset = rulesets.get(cycleOperatingZoneLog.userHosRuleSet.id);
        if (ruleset) {
            const ruleSetInfo = getRuleSetById(ruleset.hosRuleSet.id);
            if (ruleSetInfo) {
                // Any of these conditions being true means we have not yet gotten the updated ruleset
                if (cycleOperatingZoneLog.isCycleChangeLog()) {
                    if (getCycle(ruleset.hosRuleSet.id) !== cycleOperatingZoneLog.status) {
                        return false;
                    }
                } else {
                    if (getOperatingZone(ruleSetInfo.name) !== cycleOperatingZoneLog.status) {
                        return false;
                    }
                }
            }
        } else {
            // Ruleset is not yet downloaded
            return false;
        }
    }

    return true;
}

/**
 * Gets unverified zone/cycle change logs from the duty status log service.
 *
 * @param app - The AppView instance
 * @returns Array of unverified zone/cycle change logs
 */
export function getUnverifiedZoneCycleLogs(app: AppView): DutyStatusLog[] {
    if (!app.dutyStatusLogService) {
        return [];
    }

    return app.dutyStatusLogService.getSortedHosLogs(
        app.dutyStatusLogService.getHOSLogs(false, undefined, false, true)
            .filter((changeLog: DutyStatusLog) => !changeLog.verifyDateTime && changeLog.isCycleOrZoneChangeLog())
    );
}

/**
 * Gets the structured display data for a single log entry.
 *
 * @param log - The duty status log
 * @param translate - Translation function
 * @returns Structured data for display, or null if status is unknown
 */
function getLogEntryData(log: DutyStatusLog, translate: (key: string) => string): IChangeLogEntry | null {
    const isCycle = log.isCycleChangeLog();
    const labelKey = isCycle
        ? ZoneCycleChangeDialogTranslationsDictionary.Cycle
        : ZoneCycleChangeDialogTranslationsDictionary.Zone;
    const label = translate(labelKey);
    const type = isCycle ? "cycle" : "zone";
    const status = log.status;

    switch (status) {
        case "CanadaCycleOne":
            return { type, label, value: translate(ZoneCycleChangeDialogTranslationsDictionary.Cycle1) };
        case "CanadaCycleTwo":
            return { type, label, value: translate(ZoneCycleChangeDialogTranslationsDictionary.Cycle2) };
        case "OperatingZoneAmerica":
            return { type, label, value: translate(ZoneCycleChangeDialogTranslationsDictionary.UnitedStates) };
        case "OperatingZoneCanadaSouthOf60":
            return { type, label, value: translate(ZoneCycleChangeDialogTranslationsDictionary.CanadaSouthOf60) };
        case "OperatingZoneCanadaNorthOf60":
            return { type, label, value: translate(ZoneCycleChangeDialogTranslationsDictionary.CanadaNorthOf60) };
        default:
            return null;
    }
}

/**
 * Transforms logs to display format with formatted dates and structured entries.
 *
 * @param logsToVerify - The logs to transform
 * @param translate - Translation function
 * @returns Array of change log display data
 */
export function getChangeLogDisplayData(
    logsToVerify: DutyStatusLog[],
    translate: (key: string) => string
): IChangeLogDisplay[] {
    const logDictByDate: IChangeLogDictionary = {};

    logsToVerify.forEach(log => {
        if (!Object.prototype.hasOwnProperty.call(logDictByDate, log.dateTime)) {
            logDictByDate[log.dateTime] = {
                formattedDate: dayjs(dstCalculator.toLocalDateTime(log.getDate())).format("lll"),
                entries: []
            };
        }

        const entry = getLogEntryData(log, translate);
        if (entry) {
            logDictByDate[log.dateTime].entries.push(entry);
        }
    });

    return hashToArray(logDictByDate);
}

/**
 * Gets violation warning HTML messages.
 *
 * @param app - The AppView instance
 * @returns Promise resolving to HTML string of violation warnings
 */
export async function getViolationMessages(
    app: AppView
): Promise<string> {
    const ruleSetId = app.hosUtils.getLatestHosRuleSetIdShortened(true);
    const hasAvailability = app.hosUtils.hasAvailability(ruleSetId);

    if (!hasAvailability) {
        return "";
    }

    const ruleResult = app.hosUtils.getRuleResult(true);
    if (!ruleResult) {
        return "";
    }

    const violationErrors = app.hosUtils.checkIfInOrNearViolation(ruleResult, ruleSetId) as ViolationValidationResult[];
    if (!violationErrors.length) {
        return "";
    }

    const translator = app.translationService.getTranslator();
    const violationMessages = await Promise.all(
        violationErrors.map(violationError => violationError.getNoticeBoxMessage(translator))
    );

    return violationMessages.join("<br>");
}

/**
 * Gets structured availability display data.
 *
 * @param app - The AppView instance
 * @param translate - Translation function
 * @returns Array of availability display items, or empty array if not available
 */
export function getAvailabilityDisplayItems(
    app: AppView,
    translate: (key: string) => string
): IAvailabilityDisplayItem[] {
    const currentUser = app.userService.getCurrentUser();
    if (!currentUser) {
        return [];
    }

    const ruleSetId = app.hosUtils.getLatestHosRuleSetIdShortened(true);
    const hasAvailability = app.hosUtils.hasAvailability(ruleSetId);

    if (!hasAvailability) {
        return [];
    }

    const ruleResult = app.hosUtils.getRuleResult(true);
    if (!ruleResult) {
        return [];
    }

    const latestLog = app.hosUtils.getLatestLogForUser(currentUser as IIdEntity);
    const availabilityDisplayHelper = new AvailabilityDisplayHelper();

    const items = availabilityDisplayHelper.getDisplayItems(
        ruleResult,
        ruleSetId,
        latestLog,
        translate(ZoneCycleChangeDialogTranslationsDictionary.Days),
        "HOS",
        undefined,
        translate(ZoneCycleChangeDialogTranslationsDictionary.HoursUnit),
        translate(ZoneCycleChangeDialogTranslationsDictionary.MinutesUnit).replace("{minutes}", ""),
        translate(ZoneCycleChangeDialogTranslationsDictionary.BreakDuration),
        translate(ZoneCycleChangeDialogTranslationsDictionary.Break),
        translate(ZoneCycleChangeDialogTranslationsDictionary.BreakRequiredIn)
    );

    // getDisplayItems returns IAvailabilityDisplayItem[] for HOS mode
    return Array.isArray(items) ? items : [];
}

/**
 * Marks logs as verified and triggers sync.
 *
 * @param app - The AppView instance
 * @param logsToVerify - The logs to verify
 * @returns Promise that resolves when verification is complete
 */
export async function verifyZoneCycleLogs(
    app: AppView,
    logsToVerify: DutyStatusLog[]
): Promise<void> {
    await Promise.all(
        logsToVerify.map(log => {
            log.verify();
            return app.sync.dutyStatusLogs.setItem(log, true);
        })
    );
    await app.sync.synchronize(false, undefined, [SyncType.HOS]);
}

/**
 * Prepares all the data needed for the zone cycle change dialog.
 *
 * @param app - The AppView instance
 * @param logsToVerify - The logs to verify
 * @param translate - Translation function
 * @returns Promise resolving to dialog data or null if should not be shown
 */
export async function prepareDialogData(
    app: AppView,
    logsToVerify: DutyStatusLog[],
    translate: (key: string) => string
): Promise<IZoneCycleDialogData | null> {
    const currentUser = app.userService.getCurrentUser();
    if (!currentUser) {
        return null;
    }

    const changeLogList = getChangeLogDisplayData(logsToVerify, translate);
    if (!changeLogList.length) {
        return null;
    }

    const violationWarnings = await getViolationMessages(app);
    const availabilityItems = getAvailabilityDisplayItems(app, translate);

    return {
        changeLogList,
        violationWarnings,
        availabilityItems,
        logsToVerify
    };
}
